import TableContainer from 'components/TableContainer';
import Vocabulary from 'components/Vocabulary';
import React, { useCallback} from 'react'
import _ from 'lodash';
import ModalUtil from 'utils/modalUtil';
import { getFilterProps, Sorters } from 'utils/tableUtils';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { PermissionSetMap } from 'constants/RBACConstants';
import SlackIntegrationForm from './SlackIntegrationForm';
import AuthorisedSwitch from 'components/AuthorisedSwitch';
import WarningPopup from 'components/WarningPopup';
import ActionMenuV2 from 'components/ActionMenuV2';
import { Collapse, Tooltip, Spin, Row, Col, Typography, Button, message } from 'antd';
import { CaretDownOutlined } from '@ant-design/icons';
import AppLoader from 'components/AppLoader';
import AuthorisedContainer from 'components/AuthorisedContainer';
import DeleteButton from 'components/DeleteButton';
import { LoadingIcon } from 'components/Icons';
import { ShowMorePopover } from 'views/Insights/insightsUtils';
import EnvironmentName from 'views/GlobalSettings/EnvironmentName';
import { CellRenderers } from 'utils/tableUtils';

const items = [{
  type: "edit",
  permission: PermissionSetMap.EDIT_TOOL_CONFIG
}, {
  type: "delete",
  permission: PermissionSetMap.DELETE_TOOL
}]

export default function SlackListing({data, onUpdateConfigComponent, loginConfigList, loading, ...props}){
  const workSpaceArr = []
  if(!_.isEmpty(loginConfigList)){
    for(const workspace of loginConfigList){
        workSpaceArr.push(workspace)
    }
  }
  return (
    <Spin spinning={loading} indicator={<AppLoader />}>
      {workSpaceArr.map((workspace, i) => 
        <ConfigItem 
          loading={loading} 
          workspaceData={workspace}
          workspaceId={workspace.id}
          data={data}
          onUpdateConfigComponent={onUpdateConfigComponent}
          key={i}
          index={i} {...props} />)}
    </Spin>
  )
}

function ConfigItem({data, onUpdateConfigComponent, workspaceId, workspaceData, loading}){
  const selfData = data?.filter(config => config.workspaceId === workspaceId) || [];
  const numberofConfig = selfData.length;

  const onAddMapping = useCallback(async () => {
    ModalUtil.show({
        content: <SlackIntegrationForm workspaceId={workspaceId} config={data} onUpdateConfigComponent={onUpdateConfigComponent}/>,
    })
  }, [data, workspaceId, onUpdateConfigComponent]);

  const onDelete = async () => {
    try{
      await ToolsIntegrationsAPI.deleteSlackWorkSpace(workspaceId);
      message.success("WorkSpace Deleted Successfully")
      onUpdateConfigComponent()
    }
    catch(e){
      console.log(e)
    }
  }

  return (
  <Collapse
    className="pull-based-tool" 
    bordered={false} 
    expandIconPosition="right"
    expandIcon={({isActive}) => <Tooltip title={isActive ? "Close Mapping" : "Open Mapping" }><CaretDownOutlined rotate={isActive ? 180 : 0} style={{ fontSize: 20 }} /></Tooltip>}
  >
      <Collapse.Panel key={workspaceId} header={panelHeader(workspaceData, numberofConfig)}>
        <div className="d-flex align-items-center justify-content-flex-end m-b-sm">
          <AuthorisedContainer permission={PermissionSetMap.WRITE_TOOLS}>
            <Button size="small" type="primary" onClick={() => onAddMapping()}>Add Mapping</Button>
          </AuthorisedContainer>
          <div className='m-l-sm'>
            <AuthorisedContainer  tooltip={"Delete WorkSpace"}  permission={PermissionSetMap.DELETE_TOOL}>
              <DeleteButton onDelete={onDelete} type="icon"/>
            </AuthorisedContainer>
          </div>
        </div>
        <Spin spinning={_.isUndefined(data)} indicator={<LoadingIcon />}>
          <ConfigTable loading={loading} data={selfData} workspaceId={workspaceId} onUpdateConfigComponent={onUpdateConfigComponent} />
        </Spin>
      </Collapse.Panel>
  </Collapse>
  )
}

function ConfigTable({ data, onUpdateConfigComponent, workspaceId }) {
  const ToggleButton = ({text,record}) => {
    const confirmPopUp = () => {
        ModalUtil.show({
          content: <WarningPopup 
              type=""
              title={`Are you sure you want to ${record.enabled ? "disable?" : "enable?"}`} 
              onOk={() => {
                onSuspend();
          }} note />
      })
  }
    const onSuspend = () => {
        const { promise, cancel } = ToolsIntegrationsAPI.updateSlackIntegrationDetails({...record, enabled: !record.enabled}, record?.id);
        promise.then(() => onUpdateConfigComponent())
        return cancel;
    }
    return (<AuthorisedSwitch permission={PermissionSetMap.EDIT_TOOL_CONFIG} defaultChecked={text} checked={record.enabled} onClick={confirmPopUp} size="small"/>)
  }

  const onDelete = async (record) => {
    try {
      await ToolsIntegrationsAPI.deleteSlackConfiguration(record.id);
      onUpdateConfigComponent();
    } catch (err) {
      console.log(err)
    }
  }
  
  const onClick = async (_, record) => {
    switch(record.name){
      case "edit":
          ModalUtil.show({
            content: <SlackIntegrationForm config={record.data} workspaceId={workspaceId} isEdit={true} onUpdateConfigComponent={onUpdateConfigComponent}/>,
          });
        break;
      case "delete":
          await onDelete(record.data)
        break;
      default:
        break;
    }
  }

  const columns = [{
    title: <Vocabulary name="product" />,
    dataIndex: "apName",
    sorter: Sorters.STRING("apName"),
    ...getFilterProps({ dataIndex: "apName", dataSource: data, nullFilterValue: null, filterLabelFormatter: (value) => value === CellRenderers.VALUE_OR_NA() ? "All" : value}),
    render: (text) =>  text || 'All',
    width: 150
  }, {
    title: <Vocabulary name="subProduct" />,
    dataIndex: "aspName",
    sorter: Sorters.STRING("aspName"),
    ...getFilterProps({ dataIndex: "aspName", dataSource: data, nullFilterValue: null, filterLabelFormatter: (value) => value === CellRenderers.VALUE_OR_NA() ? "All" : value}),
    render: (aspName) => aspName || "All",
    width: 150
  }, {
    title: "Environment",
    dataIndex: "aeName",
    ...getFilterProps({ dataIndex: "aeName", filterLabelFormatter: (aeName) => CellRenderers.VOCABULARY_RENDERER(aeName), dataSource: data }),
    ellipsis: true,
    width: 150,
    render: (aeName) => aeName ? <EnvironmentName name = {aeName} /> : "All",
  }, {
    title: "Channel Name",
    dataIndex: "channels",
    ...getFilterProps({ filters: [...new Set(_.flatten(_.map(data, entry => Object.values(entry.channels))))], noFilterFunction: true }),
    onFilter: (value, record) => {
      return !!_.values(record.channels).find((entry) => entry === value);
    },
    render: (channels) =>  ShowMorePopover(Object.values(channels), channels, 2, _.noop, 'tag'),
    ellipsis: true
  }, {
    title: 'Config',
    dataIndex: 'enabled',
    width: 100,
    render:(text,record)=>{
      return <ToggleButton text={text} record={record} />
    }
  },{
    title: "Action",
    dataIndex: "id",
    key: 'action',
    width: 50,
    render: (id, obj) => {
      return (
        <ActionMenuV2 items={items} onClick={onClick} data={obj} />
      )
    },
  }]

  return (
    <TableContainer
      columns={columns}
      dataSource={data}
      loading={_.isUndefined(data)}
      locale={{ emptyText: "No configurations" }}
    />
  )
}

function panelHeader(workspaceData, numberofConfig){
  const {teamName} = workspaceData;
  return (
    <Row className="align-items-center">
      <Col span={20}>
        {teamName && <Typography.Text className="m-r-l">Workspace: {teamName}</Typography.Text>}
        {<Typography.Text className="m-r-l">{numberofConfig === 0 ? "No Mappings Available" :`Mappings: ${numberofConfig}`}</Typography.Text>}
      </Col>
    </Row>
  )
}
